import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  additionalSection?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Tradition as Identity and Strength',
    content: [
      '<strong>Objective:</strong> Explore how traditional practices sustain personal and community well-being.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• Tradition as a source of stability and resilience',
      '• Family and community ties as protective factors',
      '• Honoring ancestors and heritage',
      '• The role of rituals, symbols, and customs in well-being',
      '<br/>',
      '<strong>Discussion:</strong> Tradition is more than ritual – it\'s continuity. It gives life rhythm and meaning. For many, respecting elders, keeping family rituals, or celebrating cultural festivals are ways of staying connected to history and belonging.'
    ]
  },
  {
    title: 'Tradition as Stability',
    content: [
      'Tradition provides stability and resilience by offering familiar practices and values that anchor us during change.',
      '<br/>',
      'It helps maintain mental and emotional balance.'
    ]
  },
  {
    title: 'Family and Community Ties',
    content: [
      'Family and community ties are protective factors. They provide support, belonging, and shared wisdom.',
      '<br/>',
      'Strong ties reduce isolation and enhance coping abilities.'
    ]
  },
  {
    title: 'Honoring Ancestors and Heritage',
    content: [
      'Honoring ancestors and heritage connects us to our roots, fostering pride and continuity.',
      '<br/>',
      'This practice builds resilience through cultural identity.'
    ]
  },
  {
    title: 'Rituals, Symbols, and Customs',
    content: [
      'Rituals, symbols, and customs play a key role in well-being by providing meaning, structure, and emotional expression.',
      '<br/>',
      'They strengthen community bonds and personal identity.'
    ]
  },
  {
    title: 'Activity and Creative Task',
    content: [
      'Identify one tradition that defines your family or community. What value does it represent (e.g., love, respect, perseverance)?',
      '<br/>',
      'Creative Task: Write a short reflection titled "A Tradition That Taught Me Strength."'
    ],
    additionalSection: {
      title: 'Reflection',
      content: [
        'Reflect on how traditions have shaped your identity and provided strength.'
      ]
    }
  },
  {
    title: 'Key Takeaway',
    content: [
      'Tradition preserves wisdom – it reminds us of what has sustained generations before us.'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You have completed Module 4: Tradition as Identity and Strength! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about tradition and identity.',
        'The quiz will help you review key concepts about traditional practices and well-being.'
      ]
    }
  }
];

const ReligioTraditioCulturalModule4Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('rtcM4');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/religio-traditio-cultural-module-4/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/religio-traditio-cultural-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left religio-traditional-cultural-panel"
        >
          <div className="module-header">
            <h1>Module 4</h1>
            <p className="module-subtitle">Tradition as Identity and Strength</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Explore how traditional practices sustain personal and community well-being.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {safeCurrentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/religio-traditio-cultural-module-4/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReligioTraditioCulturalModule4Page;


